import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Modal, Tooltip } from "antd";
import BsDropdown from "./BsDropdown";
import {
  CheckOutlined,
  DownOutlined,
  SwapOutlined,
  LoadingOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./BranchSwitcher.css";
import { getUserRoleDisplayName } from "../userRoleConstants";
import { getBranchColor } from "./colors";
import { BRANCHES_CHANGED_EVENT } from "./events";
import BranchAvatar from "./BranchAvatar";
import { expireAuthAndRedirectToLogin } from "../syncStrataSession.js";

const CUSTOMER_DISABLED_CODE = 1503;

/**
 * BranchSwitcher - 组织切换组件（完整自包含）
 *
 * Props:
 *   currentCompanyName  - 当前组织名称
 *   token              - 认证 token
 *   onSwitch           - 切换回调 (newToken, customerId, companyName) => void
 *   mode               - 'auto' | 'dropdown' | 'modal'，默认 'auto' 根据屏幕宽度自动选择
 *
 * Ref 方法:
 *   openModal()        - 外部调用打开 Mobile Modal（用于 Drawer 菜单入口）
 */
const BranchSwitcher = forwardRef(
  (
    {
      currentCompanyName = "",
      currentCustomerId = "",
      token = "",
      onSwitch,
      mode = "auto",
      collapsed = false,
    },
    ref,
  ) => {
    const [branches, setBranches] = useState([]);
    const [switching, setSwitching] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    // Dropdown 开关状态（用于控制 Tooltip 显示）
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // 暴露 openModal 给父组件调用
    useImperativeHandle(ref, () => ({
      openModal: () => setModalOpen(true),
    }));

    // 自动检测模式：宽度 < 576 用 modal
    const isMobileMode =
      mode === "modal" ||
      (mode === "auto" &&
        typeof window !== "undefined" &&
        window.innerWidth < 576);
    const useDropdown = !isMobileMode;

    const loadBranches = useCallback((authToken) => {
      const t =
        authToken ||
        (typeof window !== "undefined" &&
          window.sessionStorage.getItem("ZeitroA")) ||
        token;
      if (!t) return Promise.resolve();
      return fetch("/api/v1/los/auth/get-branches", {
        headers: { Authorization: `Bearer ${t}` },
      })
        .then((r) => {
          if (r.status === 401) {
            expireAuthAndRedirectToLogin();
            return Promise.reject(new Error("session expired"));
          }
          return r.json();
        })
        .then((data) => {
          if (data.code === 0 && data.data) setBranches(data.data);
        });
    }, [token]);

    // ========== 数据加载 ==========
    useEffect(() => {
      if (!token) return;
      let cancelled = false;
      loadBranches(token)
        .then(() => {
          if (!cancelled) setLoaded(true);
        })
        .catch(() => {
          if (!cancelled) setLoaded(true);
        });
      return () => {
        cancelled = true;
      };
    }, [token, loadBranches]);

    useEffect(() => {
      const onBranchesChanged = () => {
        loadBranches(token).catch(() => {});
      };
      window.addEventListener(BRANCHES_CHANGED_EVENT, onBranchesChanged);
      return () => {
        window.removeEventListener(BRANCHES_CHANGED_EVENT, onBranchesChanged);
      };
    }, [loadBranches, token]);

    // ========== 切换逻辑 ==========
    const switchTo = useCallback(
      async (targetId) => {
        if (!token || switching) return;
        setSwitching(targetId);
        try {
          const res = await fetch("/api/v1/los/auth/switch-branch", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ targetCustomerId: targetId }),
          });

          if (res.status === 401) {
            expireAuthAndRedirectToLogin();
            return;
          }

          const data = await res.json();
          if (data.code === CUSTOMER_DISABLED_CODE) {
            await loadBranches(token);
            return;
          }

          if (data.code === 0 && data.data && typeof onSwitch === "function") {
            onSwitch(
              data.data.token,
              data.data.customerId,
              data.data.companyName,
            );
            setModalOpen(false); // 切换成功后关闭 Modal
          }
        } catch (_) {
        } finally {
          setSwitching(null);
        }
      },
      [token, switching, onSwitch, loadBranches],
    );

    // ========== 工具方法 ==========

    /**
     * 格式化角色数组为可读字符串
     * @param {string[]} roles - 角色数组，如 ["LoanOfficer", "Owner"]
     * @returns {string} 格式化后的字符串，如 "Loan Officer, Owner"
     */
    const formatRoles = (roles) => {
      if (!roles || !Array.isArray(roles) || roles.length === 0) return "";

      return roles
        .map((role) => getUserRoleDisplayName(role))
        .join(", ");
    };

    /**
     * 生成 Tooltip 内容（React 节点）
     * - 第一行：组织名称（正常样式）
     * - 第二行：Roles（小字号 + 浅灰色）
     * @param {string} companyName - 组织名称
     * @param {string[]} roles - 角色数组
     * @returns {ReactNode|string} Tooltip 内容
     */
    const buildTooltipContent = (companyName, roles) => {
      if (!roles || roles.length === 0) return companyName;

      return (
        <div className="bs-tooltip-content">
          <div className="bs-tooltip-name">{companyName}</div>
          <div className="bs-tooltip-roles">{formatRoles(roles)}</div>
        </div>
      );
    };

    const color = (index) => {
      return getBranchColor(index);
    };

    // ========== 渲染：无 token ==========
    if (!token) {
      return (
        <div
          className={`bs-trigger${collapsed ? " bs-trigger--compact" : ""}`}
          style={{ minHeight: 40 }}
        >
          <BranchAvatar name={currentCompanyName} color={color(0)} />
          {!collapsed && <span className="bs-text">{currentCompanyName}</span>}
        </div>
      );
    }

    // ========== 渲染：加载中（仅 Desktop） ==========
    if (!loaded && !isMobileMode) {
      return (
        <div className={`bs-trigger bs-trigger--skeleton${collapsed ? " bs-trigger--compact" : ""}`}>
          <BranchAvatar color="#e0e0e0" />
          {!collapsed && (
            <>
              <span className="bs-text bs-skeleton-shimmer">
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </span>
              {useDropdown && (
                <DownOutlined
                  className="bs-arrow"
                  style={{ visibility: "hidden" }}
                />
              )}
            </>
          )}
        </div>
      );
    }

    // ========== 渲染：单组织 ==========
    if (branches.length <= 1) {
      // 收起模式：只显示圆点
      if (collapsed) {
        const compactBranch = branches.find((b) => b.customerId === currentCustomerId);
        const compactRoles = compactBranch?.roles || [];

        return (
          <Tooltip overlayClassName="bs-tooltip" title={buildTooltipContent(currentCompanyName, compactRoles)} placement="right">
            <div className="bs-trigger bs-trigger--compact">
              <BranchAvatar name={currentCompanyName} color={color(0)} />
            </div>
          </Tooltip>
        );
      }
      // 宽屏：静态展示 + Tooltip（名称可能被截断）
      if (useDropdown) {
        const staticBranch = branches.find((b) => b.customerId === currentCustomerId);
        const staticRoles = staticBranch?.roles || [];

        return (
          <Tooltip overlayClassName="bs-tooltip" title={buildTooltipContent(currentCompanyName, staticRoles)} placement="right">
            <div className="bs-trigger bs-trigger--static">
              <BranchAvatar name={currentCompanyName} color={color(0)} />
              <div className="bs-trigger-info">
                <span className="bs-text">{currentCompanyName}</span>
                {staticRoles.length > 0 && (
                  <span className="bs-trigger-roles">{formatRoles(staticRoles)}</span>
                )}
              </div>
            </div>
          </Tooltip>
        );
      }
      // 移动端：可打开 Modal 查看当前组织（只读）
      return (
        <>
          <div
            className="nav"
            onClick={() => setModalOpen(true)}
          >
            Switch Organizations
          </div>

          <Modal
            title="Current Organization"
            open={modalOpen}
            onCancel={() => setModalOpen(false)}
            footer={null}
            centered
            className="branch-switch-modal"
            destroyOnHide
          >
            {!loaded ? (
              <div className="branch-loading">
                <LoadingOutlined spin style={{ fontSize: 24 }} />
                <span style={{ marginTop: 12, color: "#999" }}>Loading...</span>
              </div>
            ) : (
            <div className="branch-list">
              {branches.map((b, index) => {
                const isActive = b.customerId === currentCustomerId;
                return (
                  <div key={b.customerId} className="branch-item branch-item--active branch-item--readonly">
                    <BranchAvatar name={b.companyName} color={color(index)} className="branch-dot" />
                    <div className="branch-info">
                      <span className="branch-name">{b.companyName}</span>
                      {b.roles && b.roles.length > 0 && (
                        <span className="branch-roles">{formatRoles(b.roles)}</span>
                      )}
                    </div>
                    <span className="branch-current">
                      <SwapOutlined /> Current
                    </span>
                  </div>
                );
              })}
            </div>
            )}
          </Modal>
        </>
      );
    }

    // ========== 渲染：多组织 - Desktop 自定义 Dropdown ==========
    // 下拉面板内容（复用给 BsDropdown）
    const dropdownOverlay = (
      <div className="ant-dropdown-menu">
        <div className="bs-header">
          <span>SWITCH ORGANIZATION</span>
          <button
            type="button"
            className="bs-header-close"
            aria-label="Close organization switcher"
            onClick={() => setDropdownOpen(false)}
          >
            <CloseOutlined />
          </button>
        </div>
        {branches.map((b, index) => {
          const isActive = b.customerId === currentCustomerId;
          const isItemSwitching = switching === b.customerId;
          return (
            <div
              key={b.customerId}
              className={`ant-dropdown-menu-item${isActive ? " ant-dropdown-menu-item-active" : ""}`}
              onClick={() => {
                if (!isActive && !isItemSwitching) {
                  setDropdownOpen(false);
                  switchTo(b.customerId);
                }
              }}
            >
              <div className="bs-row">
                <BranchAvatar name={b.companyName} color={color(index)} />
                <div className="bs-info">
                  <span className="bs-name">{b.companyName}</span>
                  {b.roles && b.roles.length > 0 && (
                    <span className="bs-roles">{formatRoles(b.roles)}</span>
                  )}
                </div>
                {isItemSwitching ? (
                  <LoadingOutlined className="bs-check bs-check--loading" spin />
                ) : isActive ? (
                  <CheckOutlined className="bs-check" />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );

    // 触发器内部内容
    // 获取当前组织的角色信息和索引
    const currentBranch = branches.find((b) => b.customerId === currentCustomerId);
    const currentIndex = branches.findIndex((b) => b.customerId === currentCustomerId);
    const currentRoles = currentBranch?.roles || [];

    const triggerInner = switching ? (
      <>
        <BranchAvatar color={color(currentIndex)}>
          <LoadingOutlined spin />
        </BranchAvatar>
        {!collapsed && <span className="bs-text">Switching...</span>}
      </>
    ) : (
      <>
        <BranchAvatar name={currentCompanyName} color={color(currentIndex)} />
        {!collapsed && (
          <div className="bs-trigger-info">
            <span className="bs-text">{currentCompanyName}</span>
            {currentRoles.length > 0 && (
              <span className="bs-trigger-roles">{formatRoles(currentRoles)}</span>
            )}
          </div>
        )}
        {!collapsed && <DownOutlined className="bs-arrow" />}
      </>
    );

    // 触发器容器样式
    const triggerClassName = `bs-trigger${collapsed ? " bs-trigger--compact" : ""}${switching ? " bs-trigger--switching" : ""}`;

    if (useDropdown) {
      // Dropdown 打开时不包 Tooltip，彻底避免冲突
      const triggerEl = <div className={triggerClassName}>{triggerInner}</div>;

      return (
        <BsDropdown
          placement={collapsed ? "rightBottom" : "bottomLeft"}
          overlay={dropdownOverlay}
          open={dropdownOpen}
          overlayClassName="bs-drop"
          disabled={switching !== null}
          onOpenChange={(open) => setDropdownOpen(open)}
        >
          {dropdownOpen ? triggerEl : (
            <Tooltip overlayClassName="bs-tooltip" title={buildTooltipContent(currentCompanyName, currentRoles)} placement="right">
              {triggerEl}
            </Tooltip>
          )}
        </BsDropdown>
      );
    }

    // ========== 渲染：多组织 - Mobile 触发器 + Modal ==========
    return (
      <>
        <div
          className="nav"
          onClick={() => setModalOpen(true)}
        >
          Switch Organizations
        </div>

        <Modal
          title="Switch Organization"
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          footer={null}
          centered
          className="branch-switch-modal"
          destroyOnHide
        >
          {!loaded ? (
            <div className="branch-loading">
              <LoadingOutlined spin style={{ fontSize: 24 }} />
              <span style={{ marginTop: 12, color: "#999" }}>Loading organizations...</span>
            </div>
          ) : (
            <div className="branch-list">
            {branches.map((b, index) => {
              const isActive = b.customerId === currentCustomerId;
              const isItemSwitching = switching === b.customerId;
              return (
                <div
                  key={b.customerId}
                  className={`branch-item${isActive ? " branch-item--active" : ""}`}
                  onClick={() =>
                    !isActive && !isItemSwitching && switchTo(b.customerId)
                  }
                >
                  <BranchAvatar name={b.companyName} color={color(index)} className="branch-dot" />
                  <div className="branch-info">
                    <span className="branch-name">{b.companyName}</span>
                    {b.roles && b.roles.length > 0 && (
                      <span className="branch-roles">{formatRoles(b.roles)}</span>
                    )}
                  </div>
                  {isActive && (
                    <span className="branch-current">
                      <SwapOutlined /> Current
                    </span>
                  )}
                  {isItemSwitching && (
                    <span className="branch-switching">
                      <LoadingOutlined /> Switching...
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </Modal>
      </>
    );
  },
);

export default BranchSwitcher;
